/* Native Shopify predictions, shared by the header and full search page. */
((scope) => {
  'use strict';
  const categories = ['gaming-pcs', 'components', 'accessories'];
  const normalize = value => String(value || '').trim().replace(/\s+/g, ' ');
  const resourceType = category => category === 'help' ? 'page,article'
    : category === 'all' ? 'product,page,article' : 'product';
  function productTypes(category, values) {
    return [...new Set((Array.isArray(values) ? values : []).map(normalize).filter(Boolean))]
      .filter(value => !/warrant|shipping|delivery/i.test(value)
        && (category === 'gaming-pcs' || !/^pc[ -]?cases?$/i.test(value)));
  }
  function scopedQuery(query, category, types) {
    const text = normalize(query);
    const allowed = productTypes(category, types);
    return text && categories.includes(category) && allowed.length
      ? `${text} AND (${allowed.map(type => 'product_type:' + JSON.stringify(type)).join(' OR ')})` : text;
  }
  function searchUrl(action, query, category, types, values = []) {
    const url = new URL(action, scope.location?.origin || 'https://elitepcs.co.uk');
    for (const [key, value] of values) if (!['q','type','elite_scope','page'].includes(key)) url.searchParams.append(key, value);
    url.searchParams.set('q', scopedQuery(query, category, types));
    url.searchParams.set('type', resourceType(category));
    url.searchParams.set('elite_scope', category);
    if (!url.searchParams.has('options[prefix]')) url.searchParams.set('options[prefix]', 'last');
    return url.pathname + url.search;
  }
  const api = {productTypes, scopedQuery, searchUrl, resourceType};
  if (typeof module === 'object' && module.exports) { module.exports = api; return; }
  if (scope.ElitePredictiveSearch) return;
  scope.ElitePredictiveSearch = api;
  const cache = new Map();
  const common = ['RTX 5060 gaming PC','RTX 5070 gaming PC','RTX 5080 gaming PC',
    'Ryzen gaming PC','white gaming PC','gaming PCs under £1000','32GB RAM','DDR5 RAM',
    'NVMe SSD','gaming monitor','gaming keyboard','gaming mouse','gaming headset',
    'PC warranty','delivery and returns','contact support'];

  async function request(url, signal) {
    const stored = cache.get(url);
    if (stored && stored.expires > Date.now()) return stored.html;
    const response = await fetch(url, {signal, headers:{Accept:'text/html'}});
    if (!response.ok) throw new Error('Predictions unavailable');
    const html = await response.text();
    if (!html.includes('data-predictive-response')) throw new Error('Invalid predictions');
    cache.set(url, {html, expires:Date.now() + 60000});
    while (cache.size > 30) cache.delete(cache.keys().next().value);
    return html;
  }
  function init(form) {
    if (form.dataset.predictiveReady) return;
    const host = form.parentElement;
    const input = form.querySelector('input[type="search"]');
    const category = form.querySelector('[data-search-scope]');
    const panel = host.querySelector('[data-predictive-results]');
    if (!input || !category || !panel) return;
    form.dataset.predictiveReady = 'true';
    const suggestions = panel.querySelector('[data-predictive-suggestions]');
    const matches = panel.querySelector('[data-predictive-matches]');
    const all = panel.querySelector('[data-predictive-all]');
    const status = host.querySelector('[data-predictive-status]');
    const chips = host.querySelector('[aria-label="Popular searches"]');
    const dialog = form.closest('[data-search-dialog]');
    let timer, controller, serial = 0, composing = false, active = -1;
    const initial = new URLSearchParams(scope.location.search).get('elite_scope');
    if ([...category.options].some(option => option.value === initial)) category.value = initial;
    if (form.classList.contains('elite-search-page__form') && categories.includes(initial)) {
      document.title = `Search: ${normalize(input.value)} | ElitePCS`;
    }
    input.setAttribute('role','combobox');
    input.setAttribute('aria-autocomplete','list');
    input.setAttribute('aria-haspopup','listbox');
    input.setAttribute('aria-expanded','false');
    input.setAttribute('aria-controls',panel.id);
    panel.setAttribute('role','listbox');
    panel.setAttribute('aria-label','Search suggestions and matching results');
    const types = () => {
      try { return JSON.parse(category.selectedOptions[0]?.dataset.productTypes || '[]'); } catch { return []; }
    };
    const destination = (query = input.value) => searchUrl(form.action, query, category.value, types(), new FormData(form));
    const links = () => [...panel.querySelectorAll('a[href]')].filter(link => !link.hidden);
    function fitResults() {
      if (!dialog) return;
      const shell = host;
      const height = scope.visualViewport?.height || scope.innerHeight;
      dialog.style.setProperty('--elite-search-height', `${height}px`);
      if (panel.hidden) return;
      const rows = [...suggestions.querySelectorAll('a'), ...matches.querySelectorAll('a')];
      const headings = [...panel.querySelectorAll('h3')];
      const suggestionColumn = suggestions.closest('.elite-predictive__suggestions');
      if (suggestionColumn) suggestionColumn.hidden=false;
      matches.hidden=false;
      rows.forEach(row => { row.hidden=false; });
      headings.forEach(heading => { heading.hidden=false; });
      active=-1; input.removeAttribute('aria-activedescendant');
      rows.forEach(row=>{row.classList.remove('is-active');row.setAttribute('aria-selected','false');});
      const updateHeadings = () => {
        const suggestionHeading = panel.querySelector('.elite-predictive__suggestions > h3');
        const hasSuggestions = [...suggestions.children].some(row => !row.hidden);
        if (suggestionHeading) suggestionHeading.hidden = !hasSuggestions;
        if (suggestionColumn) suggestionColumn.hidden = !hasSuggestions;
        for (const heading of matches.querySelectorAll('h3')) {
          let row=heading.nextElementSibling, visible=false;
          while (row && row.tagName!=='H3') { if (!row.hidden) visible=true; row=row.nextElementSibling; }
          heading.hidden=!visible;
        }
        if (matches.querySelector('a')) matches.hidden=![...matches.querySelectorAll('a')].some(row=>!row.hidden);
      };
      // Keep the complete modal in view. Short screens get fewer preview rows;
      // the View all link always opens the complete, category-scoped results.
      for (let remaining=rows.length; remaining>0; remaining--) {
        if (shell.scrollHeight<=shell.clientHeight+1 && shell.getBoundingClientRect().bottom<=height-12) break;
        const queryRows=[...suggestions.querySelectorAll('a')].filter(row=>!row.hidden);
        const resultRows=[...matches.querySelectorAll('a')].filter(row=>!row.hidden);
        const stacked=scope.matchMedia('(max-width: 600px)').matches;
        const queriesTaller=suggestions.getBoundingClientRect().height>matches.getBoundingClientRect().height;
        const candidates=stacked ? (resultRows.length>queryRows.length ? resultRows : queryRows)
          : (queriesTaller ? queryRows : resultRows);
        const row=candidates.at(-1) || resultRows.at(-1) || queryRows.at(-1);
        if (!row) break;
        row.hidden=true; updateHeadings();
      }
      shell.scrollTop=0;
      if (status) status.textContent=`${[...suggestions.querySelectorAll('a')].filter(row=>!row.hidden).length} suggestions and ${[...matches.querySelectorAll('a')].filter(row=>!row.hidden).length} matching results`;
    }
    function hide() {
      panel.hidden = true;
      suggestions.replaceChildren(); matches.replaceChildren();
      input.setAttribute('aria-expanded','false'); input.removeAttribute('aria-activedescendant');
      active = -1;
      if (chips) chips.hidden = false;
      if (status) status.textContent = '';
      dialog?.classList.toggle('is-searching', Boolean(normalize(input.value)));
    }
    function cancel() { clearTimeout(timer); controller?.abort(); serial++; }
    function move(index) {
      const items = links();
      active = index;
      items.forEach((link, i) => {
        const selected = i === active;
        link.classList.toggle('is-active', selected);
        link.setAttribute('aria-selected',String(selected));
      });
      if (items[active]) {
        input.setAttribute('aria-activedescendant',items[active].id);
        items[active].scrollIntoView({block:'nearest'});
      } else input.removeAttribute('aria-activedescendant');
    }
    function render(query, mainHtml, queryHtml) {
      const doc = new DOMParser().parseFromString(mainHtml,'text/html');
      const queryDoc = queryHtml ? new DOMParser().parseFromString(queryHtml,'text/html') : doc;
      const productNodes = [...doc.querySelectorAll('[data-predictive-products] > a')]
        .filter(node => !categories.includes(category.value) || node.dataset.searchCategory === category.value).slice(0,5);
      const helpNodes = ['all','help'].includes(category.value)
        ? [...doc.querySelectorAll('[data-predictive-help] > a')].slice(0,3) : [];
      const queryTerms = [];
      try { queryTerms.push(...JSON.parse(queryDoc.querySelector('[data-predictive-query-data]')?.textContent || '[]')); } catch {}
      const products = category.value === 'help' ? [] : productNodes;
      const fallbacks = products.map(node=>node.querySelector('strong')?.textContent).filter(Boolean);
      const unique = [...new Set([...queryTerms,...common.filter(term => term.toLowerCase().includes(query.toLowerCase())),...fallbacks,query]
        .filter(term => typeof term === 'string' && term.trim() && term.length <= 200))].slice(0,6);
      suggestions.replaceChildren(); matches.replaceChildren();
      for (const term of unique) {
        const link = document.createElement('a');
        link.href = destination(term); link.textContent = term;
        suggestions.append(link);
      }
      const group = (title, nodes) => {
        if (!nodes.length) return;
        const heading = document.createElement('h3'); heading.textContent = title; matches.append(heading);
        for (const node of nodes) {
          const target = new URL(node.getAttribute('href'),scope.location.origin);
          if (target.origin !== scope.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:products|pages|blogs)\//i.test(target.pathname)) continue;
          matches.append(document.importNode(node,true));
        }
      };
      group('Products',products); group('Help & guides',helpNodes);
      if (!products.length && !helpNodes.length) {
        const empty = document.createElement('p'); empty.className='elite-predictive__empty';
        empty.textContent = 'No matching results. Try another term or view all search results.'; matches.append(empty);
      }
      all.href = destination();
      all.replaceChildren(document.createTextNode(`View all results for “${query}”`));
      const arrow = document.createElement('span'); arrow.setAttribute('aria-hidden','true'); arrow.textContent='→';all.append(arrow);
      links().forEach((link,i) => { link.id=`${panel.id}-option-${i}`;link.setAttribute('role','option');link.setAttribute('aria-selected','false'); });
      panel.hidden = false; input.setAttribute('aria-expanded','true');
      input.removeAttribute('aria-activedescendant'); active=-1;
      if (chips) chips.hidden=true;
      if (status) status.textContent=`${unique.length} suggestions and ${products.length + helpNodes.length} matching results`;
      dialog?.classList.add('is-searching');
      fitResults();
    }
    async function update(run) {
      const query = normalize(input.value);
      if (query.length < 1 || (dialog && dialog.hidden)) return;
      controller = new AbortController();
      const signal=controller.signal;
      const endpoint = new URL(form.dataset.predictiveUrl, scope.location.origin);
      const build = (text, resources) => {
        const url=new URL(endpoint);url.searchParams.set('q',text);
        url.searchParams.set('resources[type]',resources);
        url.searchParams.set('resources[limit]','10');url.searchParams.set('resources[limit_scope]','each');
        url.searchParams.set('resources[options][unavailable_products]','last');
        url.searchParams.set('section_id','elite-predictive-results');return url.pathname+url.search;
      };
      const fullQuery=scopedQuery(query,category.value,types());
      const resources=category.value==='help'?'query,page,article':category.value==='all'?'query,product,page,article':'query,product';
      try {
        const [html, queryHtml] = await Promise.all([
          request(build(fullQuery,resources),signal),
          fullQuery !== query ? request(build(query,'query'),signal).catch(()=>null) : null
        ]);
        if (run===serial && !signal.aborted) render(query,html,queryHtml);
      } catch (error) {
        if (run!==serial || signal.aborted) return;
        // Keep ordinary search working when predictions are unavailable.
        hide();
      }
    }
    function schedule() {
      cancel(); hide();
      if (composing || normalize(input.value).length < 1) return;
      const run=serial;timer=setTimeout(()=>update(run),120);
    }
    const syncType = () => { form.querySelector('[data-search-resource-type]').value=resourceType(category.value); };
    syncType();
    input.addEventListener('input',schedule);
    input.addEventListener('compositionstart',()=>{composing=true;cancel();hide();});
    input.addEventListener('compositionend',()=>{composing=false;schedule();});
    input.addEventListener('focus',()=>{if(panel.hidden && normalize(input.value).length>=1)schedule();});
    category.addEventListener('change',()=>{syncType();schedule();});
    input.addEventListener('keydown',event=>{
      if (panel.hidden || composing) return;
      if (event.key==='ArrowDown' || event.key==='ArrowUp') {
        event.preventDefault();const count=links().length;
        if(count)move(active<0 ? (event.key==='ArrowDown'?0:count-1)
          : (active+(event.key==='ArrowDown'?1:-1)+count)%count);
      } else if (event.key==='Enter' && active>=0) {
        event.preventDefault();scope.location.assign(links()[active].href);
      } else if (event.key==='Escape') {
        event.preventDefault();event.stopPropagation();cancel();hide();
      }
    });
    form.addEventListener('submit',event=>{
      if (!normalize(input.value)) return;
      event.preventDefault();cancel();scope.location.assign(destination());
    });
    if (dialog) new MutationObserver(()=>{if(dialog.hidden){cancel();hide();}}).observe(dialog,{attributes:true,attributeFilter:['hidden']});
    if (dialog) {
      scope.addEventListener('resize',fitResults);
      scope.visualViewport?.addEventListener('resize',fitResults);
    }
  }
  function initAll(root=document) {root.querySelectorAll('[data-elite-predictive-search]').forEach(init);}
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',()=>initAll(),{once:true});else initAll();
  document.addEventListener('shopify:section:load',event=>initAll(event.target));
})(typeof window==='object'?window:{});
